import React, { useState, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { COLORS } from '@/lib/colors';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Props {
  universities: Array<{
    university: string;
    national_rank: number;
    grad_rate: number;
    retention_rate: number;
    peer_assessment: number;
    faculty_resources_rank: number;
    financial_resources_rank: number;
    [key: string]: any;
  }>;
}

const METRIC_OPTIONS = [
  { value: 'grad_rate', label: 'Graduation Rate' },
  { value: 'retention_rate', label: 'Retention Rate' },
  { value: 'peer_assessment', label: 'Peer Assessment' },
  { value: 'faculty_resources_rank', label: 'Faculty Resources Rank' },
  { value: 'financial_resources_rank', label: 'Financial Resources Rank' },
];

export function MetricsCorrelationChart({ universities }: Props) {
  const [xMetric, setXMetric] = useState('grad_rate');
  const [yMetric, setYMetric] = useState('retention_rate');

  const { regularData, depaulData } = useMemo(() => {
    const regular = [];
    let depaul = null;

    for (const uni of universities) {
      const point = {
        x: uni[xMetric],
        y: uni[yMetric],
        name: uni.university,
        rank: uni.national_rank
      };

      if (uni.university === 'DePaul University') {
        depaul = point;
      } else {
        regular.push(point);
      }
    }

    return {
      regularData: regular,
      depaulData: depaul ? [depaul] : []
    };
  }, [universities, xMetric, yMetric]);

  const options = {
    chart: {
      type: 'scatter',
      zoom: {
        enabled: true,
        type: 'xy'
      },
      fontFamily: 'inherit',
      toolbar: {
        show: false
      }
    },
    xaxis: {
      title: {
        text: METRIC_OPTIONS.find(m => m.value === xMetric)?.label,
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.text.primary
        }
      },
      tickAmount: 10,
      labels: {
        style: {
          colors: COLORS.text.primary
        }
      }
    },
    yaxis: {
      title: {
        text: METRIC_OPTIONS.find(m => m.value === yMetric)?.label,
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.text.primary
        }
      },
      tickAmount: 7,
      labels: {
        style: {
          colors: COLORS.text.primary
        }
      }
    },
    colors: [COLORS.primary.darkBlue, COLORS.accent.orange], // Regular points, DePaul point
    markers: {
      size: [6, 12], // Regular points size, DePaul point size
      strokeWidth: 1,
      strokeColors: COLORS.background.light,
      hover: {
        size: 8
      }
    },
    tooltip: {
      custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
        const point = seriesIndex === 0 ? regularData[dataPointIndex] : depaulData[dataPointIndex];
        const isDepaul = seriesIndex === 1;
        
        return `
          <div class="p-4 bg-white rounded-lg shadow-lg border">
            <div class="font-medium" style="color: ${isDepaul ? COLORS.accent.orange : COLORS.text.primary}">${point.name}</div>
            <div style="color: ${COLORS.text.secondary}">Rank: #${point.rank}</div>
            <div style="color: ${COLORS.text.secondary}">${METRIC_OPTIONS.find(m => m.value === xMetric)?.label}: ${point.x}</div>
            <div style="color: ${COLORS.text.secondary}">${METRIC_OPTIONS.find(m => m.value === yMetric)?.label}: ${point.y}</div>
          </div>
        `;
      }
    },
    grid: {
      borderColor: COLORS.border.light,
      strokeDashArray: 3
    }
  };

  const series = [
    {
      name: 'Universities',
      data: regularData
    },
    {
      name: 'DePaul University',
      data: depaulData
    }
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Select value={xMetric} onValueChange={setXMetric}>
          <SelectTrigger className="w-full border-[#4FAADD]/20 bg-white focus:ring-[#274791]/20">
            <SelectValue placeholder="Select X-Axis Metric" />
          </SelectTrigger>
          <SelectContent>
            {METRIC_OPTIONS.map(option => (
              <SelectItem 
                key={option.value} 
                value={option.value}
                className="hover:bg-[#274791]/5 focus:bg-[#274791]/5 focus:text-[#274791]"
              >
                {option.label} (X-Axis)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={yMetric} onValueChange={setYMetric}>
          <SelectTrigger className="w-full border-[#4FAADD]/20 bg-white focus:ring-[#274791]/20">
            <SelectValue placeholder="Select Y-Axis Metric" />
          </SelectTrigger>
          <SelectContent>
            {METRIC_OPTIONS.map(option => (
              <SelectItem 
                key={option.value} 
                value={option.value}
                className="hover:bg-[#274791]/5 focus:bg-[#274791]/5 focus:text-[#274791]"
              >
                {option.label} (Y-Axis)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-full h-[400px]">
        <ReactApexChart
          options={options}
          series={series}
          type="scatter"
          height={400}
        />
      </div>
    </div>
  );
}
